import { useRef } from 'react';
import { Send, Loader2, Paperclip, X } from 'lucide-react';

export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  canSend: boolean;
  isLoading: boolean;
  attachedFile: File | null;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
}

export default function ChatInput({
  value,
  onChange,
  onSend,
  canSend,
  isLoading,
  attachedFile,
  onFileSelect,
  onRemoveFile,
}: ChatInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sendBlockedReason = isLoading
    ? 'Waiting for the agent to respond...'
    : null;

  return (
    <div className="border-t border-primary/30 bg-card/50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Attached file chip */}
        {attachedFile && (
          <div className="mb-2 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-sm text-foreground">
              <Paperclip className="h-3.5 w-3.5 text-primary" />
              {attachedFile.name}
              <span className="text-muted-foreground/50 text-xs">
                ({(attachedFile.size / 1024).toFixed(0)} KB)
              </span>
              <button
                onClick={onRemoveFile}
                className="ml-1 text-muted-foreground/50 hover:text-destructive transition-colors"
                aria-label="Remove file"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </div>
        )}
        <div className="flex gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={onFileSelect}
            className="hidden"
            aria-label="Upload CSV or Excel file"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className="px-3 py-3 border border-primary/30 rounded-lg bg-background text-muted-foreground hover:text-primary hover:border-primary/50 disabled:opacity-50 transition-colors"
            aria-label="Attach file"
            title="Attach a CSV or Excel file for analysis"
          >
            <Paperclip className="h-5 w-5" />
          </button>
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend) {
                  onSend();
                }
              }
            }}
            placeholder={attachedFile ? 'Ask a question about this file...' : 'Ask a question about OOH location data...'}
            aria-label="Message input"
            className="flex-1 px-4 py-3 border border-primary/30 rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
          />
          <button
            onClick={canSend ? onSend : undefined}
            disabled={!canSend}
            className="px-6 py-3 bg-linear-to-r from-primary to-orange-400 text-white rounded-lg hover:from-primary/90 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            aria-label={isLoading ? 'Thinking...' : 'Send message'}
            title={sendBlockedReason || undefined}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Thinking...
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                Send
              </>
            )}
          </button>
        </div>
        {sendBlockedReason && value.trim() && (
          <p className="text-xs text-amber-400 mt-2">{sendBlockedReason}</p>
        )}
      </div>
    </div>
  );
}
